/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-unused-prop-types */
import React, { HTMLProps, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
  VisibilityState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { TbLink } from 'react-icons/tb';
import { toast } from 'react-toastify';
import {
  FaChevronUp,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaRegPaperPlane,
  FaInfoCircle,
} from 'react-icons/fa';
import { Connection, ConnectionType, setConnections } from '../../../redux/slices/connectionsSlice';
import localContactService from '../../../services/community/localContactService';
import { TagState } from '../../../types/Tag';
import tagService from '../../../services/community/tagService';
import ConnectionActions from '../dropdown/ConnectionActions';
import UserInfoModal from '../modals/UserInfoModal';
import InactiveReasonModal from '../modals/InactiveReasonModal';
import AddEmailModal from '../modals/AddEmailModal';
import ConnectionWithIcons from '../misc/ConnectionWithIcons';
import { store } from '../../../redux/store';
import TagDisplay from '../misc/TagDisplay';
import FilterOnTags from '../dropdown/FilterOnTags';
import { BasicInfo } from '../../../types/BasicInfo';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import i18n from '../../../locales/i18n';
import ApiImage from '../misc/ApiImage';
import { createEmptyCommunityInvite } from '../../../utils/formatUtils';
import { getProfilePictureUrl } from '../../../utils/connectionUtils';
import IconBadge from '../icons/IconBadge';
import Button from '../buttons/Button';
import ConnectionStatus from '../misc/ConnectionStatus';
import Pagination from '../misc/Pagination';
import { SocialType } from '../../../types/Profile';
import { capitalizeFirstLetter } from '../../../utils/stringUtils';
import useAdmins from '../../../hooks/community/useAdmins';
import { VerifiedBadge } from '../icons/ConnectionTypeBadge';
import connectionService from '../../../services/community/connectionService';

/**
 * @typedef {object} TableProps
 * @property {string[]} headers - The headers for the table.
 * @property {object[]} data - The data for the table.
 * @property {function} handleDelete - The function to handle the delete of a row.
 * @property {object[]} tags - The tags for the table.
 * @property {function} handleKeyPress - The function to handle the key press.
 * @property {function} handleCheckboxChange - The function to handle the checkbox change.
 */
interface TableProps {
  headers: string[];
  data: Connection[];
  tags: TagState[];
  filteredTags: string[];
  onTagClick: (tag: string) => void;
  handleCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    connection: Connection,
  ) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: OnChangeFn<VisibilityState>;
  selectAllClick: (bool: boolean) => void;
  selectedConnections: Connection[];
  /** When set, admin operations target this community instead of the Redux selectedCommunity. */
  communityId?: number;
}

interface ChildTableProps extends TableProps {
  selectAll: boolean;
  setSelectAll: (arg0: boolean) => void;
  handleDelete: (arg0: number, connectionType: ConnectionType) => Promise<void>;
  openContacts?: Connection[];
  handleOpenContacts?: (arg0: any) => void;
  deleteTag: (arg0: number, arg1: string) => Promise<void>;
  pageIndex: number;
  pageSize: 10 | 20 | 30 | 40 | 50;
  setPageIndex: (arg0: number) => void;
  setPageSize: (arg0: 10 | 20 | 30 | 40 | 50) => void;
}

const columnHelper = createColumnHelper<Connection>();

const handleDelete = async (dashboardMemberId: number): Promise<void> => {
  const promise = connectionService.deleteConnections([dashboardMemberId]);
  await toast.promise(promise!, {
    pending: 'Deleting connection...',
    success: 'Connection deleted!',
    error: 'Failed to delete connection',
  });
  setConnections(store.getState().connections);
};

const createColums = (
  headers: string[],
  tags: TagState[],
  filteredTags: string[],
  onTagClick: (id: string) => void,
  handleCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    connection: Connection,
  ) => void,
  selectAllClick: (bool: boolean) => void,
  adminIds: number[],
  community: ReturnType<typeof useSelectedCommunity>,
  setInactiveReasonData?: (data: { reason: string; connectionName?: string }) => void,
  setInactiveReasonModalOpen?: (open: boolean) => void,
  setConnectionForEmail?: (connection: Connection) => void,
  setAddEmailModalOpen?: (open: boolean) => void,
  communityId?: number,
) => {
  const columns = [];
  columns.push(
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="ml-1 px-1">
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              customOnChange: table.getToggleAllRowsSelectedHandler(),
              selectAllClick,
            }}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-1">
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              customOnChange: row.getToggleSelectedHandler(),
              handleCheckboxChange,
              connection: row.original,
            }}
          />
        </div>
      ),
      size: 60,
    }),
  );

  if (headers.find((x) => x === BasicInfo.FIRST_NAME)) {
    columns.push(
      columnHelper.accessor((x) => x, {
        id: 'name',
        header: i18n.t('general.name'),
        cell: (info) => {
          return (
            <div className="flex items-center gap-4">
              <ConnectionStatus connection={info.getValue()} interactive />
              <p className="overflow-hidden overflow-ellipsis whitespace-nowrap">{`${info.getValue()?.fields.FIRST_NAME} ${info.getValue()?.fields.LAST_NAME}`}</p>
            </div>
          );
        },
        sortingFn: (a, b) => {
          const nameA = `${a.original.fields.FIRST_NAME} ${a.original.fields.LAST_NAME}`;
          const nameB = `${b.original.fields.FIRST_NAME} ${b.original.fields.LAST_NAME}`;

          return nameA.localeCompare(nameB);
        },
      }),
    );
  }

  if (headers.find((x) => x === BasicInfo.COMMUNICATION_NAME)) {
    columns.push(
      columnHelper.accessor((x) => x.fields.COMMUNICATION_NAME, {
        id: 'communicationName',
        header: i18n.t('dataType.communicationName'),
        cell: (info) => info.getValue() || '-',
        sortingFn: 'text',
      }),
    );
  }

  if (headers.find((x) => x === BasicInfo.PHONENUMBER)) {
    columns.push(
      columnHelper.accessor((x) => x.fields.PHONENUMBER, {
        id: 'phoneNumber',
        header: i18n.t('dataType.phoneNumber'),
        cell: (info) => info.getValue() || '-',
        sortingFn: 'alphanumeric',
      }),
    );
  }

  if (headers.find((x) => x === BasicInfo.EMAIL)) {
    columns.push(
      columnHelper.accessor((x) => x.fields.EMAIL, {
        id: 'email',
        header: i18n.t('dataType.email'),
        cell: (info) => info.getValue() || '-',
        sortingFn: 'text',
      }),
    );
  }

  if (headers.find((x) => x === BasicInfo.ADDRESS)) {
    columns.push(
      columnHelper.accessor((x) => x.fields.ADDRESS, {
        id: 'address',
        header: i18n.t('dataType.address'),
        cell: (info) => <div className="whitespace-pre">{info.getValue() || '-'}</div>,
        sortingFn: 'text',
      }),
    );
  }

  if (headers.find((x) => x === BasicInfo.BUSINESSNAME)) {
    columns.push(
      columnHelper.accessor((x) => x.fields.BUSINESSNAME, {
        id: 'communityName',
        header: i18n.t('dataType.businessName'),
        cell: (info) => info.getValue() || '-',
        sortingFn: 'datetime',
      }),
    );
  }

  if (headers.find((x) => x === BasicInfo.BIRTHDATE)) {
    columns.push(
      columnHelper.accessor((x) => x.fields.BIRTHDATE, {
        id: 'birthDate',
        header: i18n.t('dataType.birthDate'),
        cell: (info) => info.getValue() || '-',
        sortingFn: 'datetime',
      }),
    );
  }
  const socials = headers.filter((header) => header in SocialType);
  socials.forEach((social) => {
    columns.push(
      columnHelper.accessor((x) => x.fields[social], {
        id: social,
        header: () => capitalizeFirstLetter(social),
        cell: (info) => info.getValue() || '-',
        sortingFn: 'text',
      }),
    );
  });

  if (headers.find((x) => x === 'role')) {
    columns.push(
      columnHelper.accessor((x) => x.userId ?? null, {
        id: 'role',
        header: i18n.t('general.role'),
        cell: (info) => {
          const userId = info.getValue();
          if (userId === null) return i18n.t('page.dashboard.table.columnLabel.generalUser');
          return adminIds.includes(userId)
            ? i18n.t('page.dashboard.table.columnLabel.admin')
            : i18n.t('page.dashboard.table.columnLabel.generalUser');
        },
        sortingFn: 'text',
      }),
    );
  }

  if (headers.find((x) => x === 'verificationLabel')) {
    columns.push(
      columnHelper.accessor((x) => x.type, {
        id: 'verificationLabel',
        header: i18n.t('page.dashboard.table.columnLabel.verificationLabel'),
        cell: (info) => {
          const connection = info.row.original;

          const isVerified = [
            ConnectionType.MEMBER
          ].includes(connection.type);

          const isPending =
            connection.type === ConnectionType.EXTERNAL_MEMBER && connection.inviteSent;

          const isUnverified =
            connection.type === ConnectionType.EXTERNAL_MEMBER && !connection.inviteSent;

          function handleInviteClick(e: React.MouseEvent<HTMLButtonElement>, connectionToInvite: Connection) {
            e.stopPropagation();
            if (connectionToInvite.fields.EMAIL) {
              localContactService.inviteContacts([connectionToInvite.externalMemberId!])
                .then(() => toast.success(i18n.t('toast.success.inviteSent_one')));
            } else if (setConnectionForEmail && setAddEmailModalOpen) {
              setConnectionForEmail(connectionToInvite);
              setAddEmailModalOpen(true);
            }
          }

          const isLinked = connection.parentExternalMemberId != null;

          const statusPill = (() => {
            if (isVerified) {
              return (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
                  {i18n.t('page.dashboard.table.columnLabel.registered')}
                </span>
              );
            }
            if (isPending) {
              return (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-900">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  {i18n.t('page.dashboard.table.columnLabel.invited')}
                </span>
              );
            }
            if (isUnverified) {
              return (
                <button
                  type="button"
                  onClick={(e) => handleInviteClick(e, connection)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800 hover:opacity-80">
                  <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                  {i18n.t('page.dashboard.table.columnLabel.notInvited')}
                </button>
              );
            }
            return <span className="text-gray-400">-</span>;
          })();

          return (
            <div className="flex items-center gap-2">
              <div className="ml-2">{statusPill}</div>
              {isLinked && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-900"
                  title={i18n.t('page.dashboard.table.columnLabel.linkedTooltip')}>
                  <TbLink className="h-3.5 w-3.5" />
                  {i18n.t('page.dashboard.table.columnLabel.linked')}
                </span>
              )}
            </div>
          );
        },
        sortingFn: 'text',
      }),
    );
  }

  if (headers.find((x) => x === 'status')) {
    columns.push(
      columnHelper.accessor((x) => x.active, {
        id: 'status',
        header: i18n.t('page.dashboard.table.columnLabel.status'),
        cell: (info) => {
          const connection = info.row.original;

          const openReason = (e: React.MouseEvent<HTMLDivElement>) => {
            if (connection.active) return;

            e.stopPropagation();
            const reason = connection.inactiveReason || i18n.t('page.dashboard.table.columnLabel.noReason');
            const connectionName = `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`;
            if (setInactiveReasonData && setInactiveReasonModalOpen) {
              setInactiveReasonData({ reason, connectionName });
              setInactiveReasonModalOpen(true);
            }
          };

          return (
            <div className="flex items-center ml-2" onClick={(e) => openReason(e)}>
                {connection.active
                  ? i18n.t('page.dashboard.table.columnLabel.active')
                  : i18n.t('page.dashboard.table.columnLabel.inactive')
                }
                {connection.active !== undefined && connection.active !== true &&
                <div className="relative group ml-1">
                  <FaInfoCircle
                    className="h-4 w-4 text-primary-300 cursor-pointer"
                  />
                </div>
              }
            </div>
          );
        },
        sortingFn: (a, b) => {
          const aActive = a.original.active;
          const bActive = b.original.active;
          if (aActive === bActive) return 0;
          return aActive ? -1 : 1;
        },
      }),
    );
  }

  const customFields = headers.filter(
    (header) =>
      !(header in BasicInfo) &&
      !(header in SocialType) &&
      header !== 'verificationLabel' &&
      header !== 'role' &&
      header !== 'status' &&
      header !== 'Tags' &&
      header !== 'BUSINESSNAME',
  );

  customFields.forEach((c) => {
    columns.push(
      columnHelper.accessor((x) => x.fields[c], {
        id: c,
        header: c,
        cell: (info) => info.getValue() || '-',
        sortingFn: 'text',
      }),
    );
  });

  columns.push(
    columnHelper.display({
      id: 'tags',
      header: () => (
        <div className="flex">
          {i18n.t('general.tags')}
          {tags.length > 0 && (
            <FilterOnTags tags={tags} onTagClick={onTagClick} filterTags={filteredTags} />
          )}
        </div>
      ),
      cell: ({ row }) => <TagDisplay connection={row.original} withHover />,
      enableSorting: false,
      enableColumnFilter: true,
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="w-8" />,
      cell: ({ row }) => (
        <div className="px-1">
          <ConnectionActions
            dashboardMemberId={row.original.dashboardMemberId}
            externalMemberId={row.original.externalMemberId}
            connectionType={row.original.type}
            invite={community?.inviteFields || createEmptyCommunityInvite()}
            handleDelete={handleDelete}
            communityId={communityId}
            connection={row.original}
          />
        </div>
      ),
    }),
  );

  return columns;
};

/**
 * A table component for the connections.
 *
 * @param {string[]} headers - The headers for the table.
 * @param {object[]} data - The data for the table.
 * @param {function} handleDelete - The function to handle the delete of a tag.
 * @param {object[]} tags - The tags for the table.
 * @param {function} handleKeyPress - The function to handle the key press for in the input field.
 * @param {function} handleCheckboxChange  - The function to handle the checkbox change.
 * @returns {JSX.Element} - The table component.
 */
export default function ConnectionsTable(props: TableProps): JSX.Element {
  const [selectAll, setSelectAll] = React.useState(false);
  const [openContacts, setOpenContacts] = React.useState<Connection[]>([]);
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<10 | 20 | 30 | 40 | 50>(50);

  useEffect(() => {
    if (pageIndex === 0) return;
    if (pageSize * pageIndex >= props.data.length)
      setPageIndex(Math.floor(props.data.length / pageSize - 1));
  }, [props.data.length]);

  const deleteTag = async (connectionId: number, tagId: string): Promise<void> => {
    const connection = props.data.find((c) => c.dashboardMemberId === connectionId);
    if (!connection) return
    await tagService.removeTagFromConnection(connectionId, tagId, connection.type);
  };
  const [isMobile, setMobile] = React.useState<boolean>(window.innerWidth < 1024);

  useEffect((): void => {
    window.addEventListener('resize', (): void => setMobile(window.innerWidth < 1024));
  }, []);

  useLayoutEffect(() => {
    setOpenContacts(
      openContacts
        .map((x) => props.data.find((y) => y.dashboardMemberId === x.dashboardMemberId && y.type === x.type))
        .filter((x) => x) as Connection[],
    );
  }, [props.data]);

  const handleOpenContacts = (connection: Connection): void => {
    if (openContacts.includes(connection)) {
      setOpenContacts(openContacts.filter((c): boolean => c !== connection));
    } else {
      setOpenContacts([...openContacts, connection]);
    }
  };

  // if (props.data.length === 0) return <div />; //todo empty state here

  if (isMobile)
    return (
      <SmallConnectionsTable
        {...props}
        selectAll={selectAll}
        handleDelete={handleDelete}
        setSelectAll={setSelectAll}
        openContacts={openContacts}
        handleOpenContacts={handleOpenContacts}
        deleteTag={deleteTag}
        pageIndex={pageIndex}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
      />
    );

  return (
    <BigConnectionsTable
      {...props}
      selectAll={selectAll}
      handleDelete={handleDelete}
      setSelectAll={setSelectAll}
      deleteTag={deleteTag}
      pageIndex={pageIndex}
      pageSize={pageSize}
      setPageIndex={setPageIndex}
      setPageSize={setPageSize}
    />
  );
}

function SmallConnectionsTable({
  data,
  selectAllClick,
  selectedConnections,
  handleCheckboxChange,
  selectAll,
  setSelectAll,
  openContacts,
  handleOpenContacts,
  pageIndex,
  pageSize,
  setPageSize,
  setPageIndex,
  communityId,
}: ChildTableProps): JSX.Element {
  const [profileImages, setProfileImages] = useState<Record<string, string>>({});

  const { t } = useTranslation();
  const community = useSelectedCommunity();

  useEffect(() => {
    const fetchProfileImages = async (): Promise<void> => {
      if (!openContacts) return;
      const images: Record<string, string> = { ...profileImages };
      await Promise.all(
        openContacts.map(async (c): Promise<void> => {
          if (!profileImages[c.dashboardMemberId]) {
            const profileImage = await getProfilePictureUrl(c).catch(() => '');
            images[c.dashboardMemberId] = profileImage;
          }
        }),
      );
      setProfileImages(images);
    };

    fetchProfileImages();
  }, [openContacts]);

  return (
    <div
      data-testid="small-connections-table"
      className="mt-2 flex flex-col gap-2 rounded-[12px]">
      <div className="flex w-full justify-between rounded-[8px] bg-primary-100 px-4 py-3">
        <input
          type="checkbox"
          data-testid="open-all"
          onChange={(): void => {
            selectAllClick(!selectAll);
            setSelectAll(!selectAll);
          }}
          checked={selectAll}
        />
        <div className="flex items-center">
          <span className="inline-block pl-1">{`${selectedConnections.length === 0
            ? ''
            : `${selectedConnections.length} ${t('component.connectionsTable.selected')}`
            }`}</span>
        </div>
      </div>
      {data
        .filter((__, index) => index >= pageIndex * pageSize && index < (pageIndex + 1) * pageSize)
        .map(
          (row: Connection): JSX.Element => (
            <div
              className={`flex h-fit w-full flex-col justify-start rounded-[8px] bg-secondary-200/70 p-4 ${selectedConnections.includes(row) ? 'ring-2 ring-primary-100' : ''
                }`}>
              <div className="flex justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    data-testid={`${row.dashboardMemberId}-checkbox`}
                    className="mr-2"
                    onChange={(event): void => {
                      if (selectAll) setSelectAll(false);
                      else if (
                        !selectedConnections.includes(row) &&
                        selectedConnections.length === data.length - 1
                      )
                        setSelectAll(true);
                      handleCheckboxChange(event, row);
                    }}
                    checked={selectedConnections.includes(row)}
                  />
                  <p className="whitespace-nowrap text-sm font-semibold text-primary-900">
                    {`${row.fields.FIRST_NAME} ${row.fields.LAST_NAME}`}
                  </p>
                  {row.inviteSent && (
                    <div className="relative h-fit w-fit">
                      <IconBadge
                        icon={FaRegPaperPlane}
                        className="peer ml-2 h-7 w-7"
                        iconClassName="h-4 w-4"
                      />
                      <div className="absolute -bottom-6 -right-16 rounded-lg bg-primary-900 p-1 px-2 text-xs text-secondary-200 opacity-0 transition-all duration-200 peer-hover:opacity-100">
                        {i18n.t('general.inviteSent')}
                      </div>
                    </div>
                  )}
                  {row.type === ConnectionType.MEMBER && (
                    <div className="peer relative ml-2 flex h-7 w-7 items-center justify-center rounded-[6px] bg-secondary-200 p-1">
                      <img src="/assets/vera_smile.svg" alt="connection" className="peer w-full" />
                      <div className="absolute -bottom-6 -right-16 w-28 rounded-lg bg-primary-900 p-1 px-2 text-xs text-secondary-200 opacity-0 transition-all duration-200 peer-hover:opacity-100">
                        {`Vera ${i18n.t('contact.connection').toLowerCase()}`}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center font-bold">
                  <ConnectionActions
                    dashboardMemberId={row.dashboardMemberId}
                    externalMemberId={row.externalMemberId}
                    connectionType={row.type}
                    invite={community?.inviteFields || createEmptyCommunityInvite()}
                    handleDelete={handleDelete}
                    communityId={communityId}
                  />
                  <button type="button" onClick={(): void => handleOpenContacts!(row)}>
                    {openContacts!.includes(row) ? (
                      <FaChevronUp
                        data-testid={`${row.dashboardMemberId}-open-contact`}
                        className="icon mx-auto h-6 w-6 font-bold"
                      />
                    ) : (
                      <FaChevronDown
                        data-testid={`${row.dashboardMemberId}-open-contact`}
                        className="icon mx-auto h-6 w-6 font-bold"
                      />
                    )}
                  </button>
                </div>
              </div>
              {openContacts!.includes(row) && (
                <div className="relative mt-2 flex w-full items-center justify-between">
                  <div className="w-full">
                    <div className="flex flex-col">
                      {profileImages[row.dashboardMemberId] && (
                        <ApiImage
                          path={profileImages[row.dashboardMemberId]}
                          alt="profile"
                          className="mb-3 mr-8 h-28 w-28 rounded-lg object-cover"
                        />
                      )}
                      <ConnectionWithIcons connection={row} />
                    </div>

                    <p className="mb-2 mt-4 text-xs font-normal text-gray-400">
                      {t('general.tags')}
                    </p>
                    <TagDisplay connection={row} />
                  </div>
                </div>
              )}
            </div>
          ),
        )}
      <Pagination
        className="pr-2"
        pageIndex={pageIndex}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        length={data.length}
      />
    </div>
  );
}

const FIRST_COLUMN_CSS = 'sticky z-[1] px-2 w-10 left-0';
const SECOND_COLUMN_CSS = 'sticky z-[1] px-2 left-10';

function BigConnectionsTable({
  data,
  headers,
  selectAllClick,
  tags,
  handleCheckboxChange,
  onTagClick,
  filteredTags,
  pageIndex,
  pageSize,
  setPageSize,
  setPageIndex,
  columnVisibility,
  setColumnVisibility,
  communityId,
}: ChildTableProps): JSX.Element {
  const [modalConnection, setModalConnection] = useState<Connection>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [inactiveReasonModalOpen, setInactiveReasonModalOpen] = useState<boolean>(false);
  const [addEmailModalOpen, setAddEmailModalOpen] = useState<boolean>(false);
  const [connectionForEmail, setConnectionForEmail] = useState<Connection | null>(null);
  const [inactiveReasonData, setInactiveReasonData] = useState<{
    reason: string;
    connectionName?: string;
  }>({ reason: '' });
  const [rowSelection, setRowSelection] = React.useState({});
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const tableData = useMemo(() => [...data], [data]);
  const adminData = useAdmins();
  const adminIds = useMemo(() => adminData.filter((a) => a.userId).map((a) => a.userId!), [adminData]);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const community = useSelectedCommunity();

  useEffect(() => {
    setModalConnection(
      data.find((x) => x.dashboardMemberId === modalConnection?.dashboardMemberId && x.type === modalConnection?.type),
    );
  }, [data]);

  const table = useReactTable({
    data: tableData,
    columns: createColums(
      headers,
      tags,
      filteredTags,
      onTagClick,
      handleCheckboxChange,
      selectAllClick,
      adminIds,
      community,
      setInactiveReasonData,
      setInactiveReasonModalOpen,
      setConnectionForEmail,
      setAddEmailModalOpen,
      communityId,
    ),
    state: {
      rowSelection,
      sorting,
      columnVisibility,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    enableRowSelection: true, //enable row selection for all rows
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    enableColumnResizing: true,
    debugTable: false,
    defaultColumn: {
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });
  return (
    <div className="relative w-full overflow-x-auto">
      <table
        style={{
          width: table.getCenterTotalSize(),
        }}
        data-testid="connections-table"
        className="z-0 h-full min-w-full border-separate border-spacing-y-2">
        <thead className="z-0">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className="z-0" key={headerGroup.id}>
              {headerGroup.headers.map((header) =>
                header.column.getIsVisible() ? (
                  <th
                    colSpan={header.colSpan}
                    key={header.id}
                    style={{
                      width: header.getSize(),
                    }}
                    className={classNames(
                      'relative bg-primary-100 py-3 pl-2 text-left text-sm font-semibold tracking-wider text-primary-900 first:rounded-l-[8px] last:rounded-r-[8px]',
                      {
                        [FIRST_COLUMN_CSS]: header.id === 'select',
                        [SECOND_COLUMN_CSS]: header.id === 'name',
                      },
                    )}>
                    {header.isPlaceholder ? null : (
                      <div
                        className={classNames('flex items-center', {
                          'cursor-pointer select-none': header.column.getCanSort(),
                        })}
                        onClick={header.column.getToggleSortingHandler()}>
                        {header.column.id === 'name' ? (
                          <div className="ml-1 mr-4">
                            <VerifiedBadge className="" />
                          </div>
                        ) : null}

                        {['select', 'actions', 'tags', 'name'].includes(header.column.id) ? (
                          flexRender(header.column.columnDef.header, header.getContext())
                        ) : (
                          <p className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </p>
                        )}

                        {header.column.getCanSort() && !header.column.getIsSorted() ? (
                          <FaSort className="ml-2 mt-0.5" />
                        ) : null}
                        {{
                          asc: <FaSortUp className="ml-2 mt-0.5" />,
                          desc: <FaSortDown className="ml-2 mt-0.5" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                    <div
                      className={classNames(
                        'absolute right-0 top-[15%] h-[70%] w-1 cursor-col-resize touch-none select-none rounded-lg hover:opacity-100',
                        {
                          'bg-primary opacity-100': header.column.getIsResizing(),
                          'bg-primary-300 opacity-10': !header.column.getIsResizing(),
                        },
                      )}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    />
                  </th>
                ) : null,
              )}
            </tr>
          ))}
        </thead>
        {table?.getRowModel().rows.length > 0 ? (
          <>
            <tbody className="z-0">
              {table?.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`group cursor-pointer ${row.getIsSelected() ? 'outline outline-2 outline-primary-100' : ''
                    }`}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={classNames(
                        'flex-justify-center max-w-0 whitespace-nowrap bg-secondary-200/70 py-3.5 pl-2 text-sm font-medium text-primary-900 first:rounded-l-[8px] last:rounded-r-[8px]',
                        {
                          'bg-secondary-200': row.getIsSelected(),
                          [FIRST_COLUMN_CSS]: cell.column.id === 'select',
                          [SECOND_COLUMN_CSS]: cell.column.id === 'name',
                        },
                      )}
                      style={{
                        width: cell.column.id === 'select' ? undefined : cell.column.getSize(),
                      }}
                      onClick={() => {
                        if (
                          cell.column.id !== 'verificationLabel' &&
                          cell.column.id !== 'select' &&
                          cell.column.id !== 'actions' &&
                          cell.column.id !== 'tags'
                        ) {
                          setModalConnection(row.original);
                          setModalOpen(true);
                        }
                      }}>
                      {['select', 'actions', 'tags', 'name'].includes(cell.column.id) ? (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      ) : (
                        <div
                          className={classNames({
                            'overflow-hidden overflow-ellipsis whitespace-nowrap':
                              cell.column.id !== 'actions' && cell.column.id !== 'tags',
                          })}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={table?.getHeaderGroups()[0].headers.length}>
                  <Pagination
                    className="justify-start"
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    setPageIndex={setPageIndex}
                    setPageSize={setPageSize}
                    length={data.length}
                  />
                </td>
              </tr>
            </tfoot>
          </>
        ) : (
          //empty state
          <tbody className="z-0">
            <tr>
              <td colSpan={table?.getHeaderGroups()[0].headers.length}>
                <div className="m-auto w-full py-3 text-center">
                  {community?.inviteFields ? (
                    <div>{t('component.connectionsTable.noConnections')}</div>
                  ) : (
                    <>
                      <div className="mb-3">{t('component.connectionsTable.noLink')}</div>
                      <Button
                        variant="primary"
                        onClick={() =>
                          navigate('/invite-link', {
                            state: { view: 'SETUP' },
                          })
                        }
                        className="mx-auto">
                        <MdEdit data-testid="share-icon" className="h-5 w-5" />
                        {t('component.connectionsTable.configureTable')}
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>

      <UserInfoModal
        open={modalOpen}
        connection={modalConnection}
        setOpen={() => setModalOpen(false)}
      />

      <InactiveReasonModal
        open={inactiveReasonModalOpen}
        setOpen={setInactiveReasonModalOpen}
        reason={inactiveReasonData.reason}
        connectionName={inactiveReasonData.connectionName}
      />

      {connectionForEmail && (
        <AddEmailModal
          open={addEmailModalOpen}
          setOpen={setAddEmailModalOpen}
          connection={connectionForEmail}
          onEmailAdded={() => {
            setConnectionForEmail(null);
          }}
        />
      )}
    </div>
  );
}

function IndeterminateCheckbox({
  indeterminate,
  customOnChange,
  handleCheckboxChange,
  selectAllClick,
  connection,
  className = '',
  ...rest
}: {
  indeterminate?: boolean;
  customOnChange: (event: unknown) => void;
  handleCheckboxChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    connection: Connection,
  ) => void;
  connection?: Connection;
  selectAllClick?: (bool: boolean) => void;
} & HTMLProps<HTMLInputElement>) {
  const ref = React.useRef<HTMLInputElement>(null!);
  React.useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);
  return (
    <input
      type="checkbox"
      ref={ref}
      className={`${className} ${!rest.checked ? 'group-hover:bg-secondary-200' : ''}`}
      onChange={(event): void => {
        customOnChange(event);
        if (handleCheckboxChange !== undefined) handleCheckboxChange(event, connection!);

        if (selectAllClick !== undefined) selectAllClick(!rest.checked!);
      }}
      {...rest}
    />
  );
}

const __test__ = {
  SmallConnectionsTable,
  BigConnectionsTable,
};
export { __test__ };
